import {
  useMemo,
  useReducer,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect
} from 'react';

import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import MoneyInputBase from '../BaseMoneyInput';
import ErrorMessageWithButton from '../ErrorMessageWithButton';
import Skeleton from '../Skeleton';

import { ERROR_REASONS } from './constants/errors';
import { INPUT_TYPES } from './constants/types';
import { formatMoneyDisplay, convertCentToDollar, getNextMonth } from './utils/format';

const EXCEED_DAILY_LIMIT_MESSAGE = (dailyRemainLimit, dailyLimit) =>
  `This exceeds your daily transfer limit of ${formatMoneyDisplay(
    convertCentToDollar(dailyLimit)
  )}. You may transfer up to ${formatMoneyDisplay(
    convertCentToDollar(dailyRemainLimit)
  )} until 7:00 pm EST today.`;

const EXCEED_MONTHLY_LIMIT_MESSAGE = (monthlyRemainLimit, monthlyLimit) =>
  `This exceeds your monthly transfer limit of ${formatMoneyDisplay(
    convertCentToDollar(monthlyLimit)
  )}. You may transfer up to ${formatMoneyDisplay(
    convertCentToDollar(monthlyRemainLimit)
  )} until ${getNextMonth()}`;

const NOT_ENOUGH_BALANCE_MESSAGE = 'This will cause you to have a negative balance.';
const EMPTY_VALUE_MESSAGE = 'Please enter an amount.';

const ACTION_TYPES = {
  SET_VALUE: 'SET_VALUE',
  SET_REASON: 'SET_REASON',
  SET_TOUCH: 'SET_TOUCH'
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_VALUE:
      return { ...state, value: action.payload };
    case ACTION_TYPES.SET_REASON:
      return { ...state, reason: action.payload };
    default:
      return state;
  }
};

const SendMoneyInput = forwardRef(function MoneyInput(
  {
    title,
    defaultValue,
    balance,
    dailyRemainLimit,
    dailyLimit,
    monthlyRemainLimit,
    monthlyLimit,
    inputType,
    addFunds,
    handleDataChange,
    fee = 0
  },
  ref
) {
  const [state, dispatch] = useReducer(reducer, {
    value: defaultValue,
    reason: null
  });
  const { reason, value } = state;

  const wireMoneyInput = inputType === INPUT_TYPES.WIRE_MONEY;

  const errorMessage = useMemo(() => {
    switch (reason) {
      case ERROR_REASONS.EMPTY_VALUE:
        return <ErrorMessageWithButton message={EMPTY_VALUE_MESSAGE} />;
      case ERROR_REASONS.NOT_ENOUGH_BALANCE:
        return (
          <ErrorMessageWithButton
            buttonText="Add funds"
            hasButton
            message={NOT_ENOUGH_BALANCE_MESSAGE}
            onClick={addFunds}
          />
        );
      case ERROR_REASONS.EXCEED_DAILY_LIMIT:
        return (
          <ErrorMessageWithButton
            message={EXCEED_DAILY_LIMIT_MESSAGE(dailyRemainLimit, dailyLimit)}
          />
        );
      case ERROR_REASONS.EXCEED_MONTHLY_LIMIT:
        return (
          <ErrorMessageWithButton
            message={EXCEED_MONTHLY_LIMIT_MESSAGE(monthlyRemainLimit, monthlyLimit)}
          />
        );
      default:
        return null;
    }
  }, [addFunds, dailyLimit, dailyRemainLimit, monthlyLimit, monthlyRemainLimit, reason]);

  /**
   * Auto validate is used when the user types in the input
   *
   * @param {number} newValue the new value of the input
   * @returns {boolean} true if the value is valid, false otherwise
   */
  const autoValidate = useCallback(
    (newValue) => {
      const valueWithFee = newValue + fee;
      // Skip validation if the value is 0
      if (newValue === 0) {
        dispatch({ type: ACTION_TYPES.SET_REASON, payload: null });
        return true;
      }

      if (balance < newValue) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.NOT_ENOUGH_BALANCE
        });
        return false;
      }

      if (balance < valueWithFee && wireMoneyInput) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.NOT_ENOUGH_BALANCE
        });
        return false;
      }

      dispatch({ type: ACTION_TYPES.SET_REASON, payload: null });
      return true;
    },
    [balance, fee, wireMoneyInput]
  );

  // Trigger auto validate when the value changes
  useEffect(() => {
    const isError = !autoValidate(value);
    handleDataChange({ value, isError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoValidate]);

  /**
   * Manual validate is used when the user clicks the submit button
   *
   * @param {number} newValue the new value of the input
   * @returns {boolean} true if the value is valid, false otherwise
   */
  const manualValidate = useCallback(
    (newValue) => {
      if (newValue === 0) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EMPTY_VALUE
        });
        return false;
      }

      if (dailyRemainLimit < newValue) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EXCEED_DAILY_LIMIT
        });
        return false;
      }

      if (monthlyRemainLimit < newValue) {
        dispatch({
          type: ACTION_TYPES.SET_REASON,
          payload: ERROR_REASONS.EXCEED_MONTHLY_LIMIT
        });
        return false;
      }

      dispatch({ type: ACTION_TYPES.SET_REASON, payload: null });
      return true;
    },
    [dailyRemainLimit, monthlyRemainLimit]
  );

  useImperativeHandle(
    ref,
    () => ({
      validate() {
        const valid = manualValidate(value);
        handleDataChange({ value, isError: !valid });
        return valid;
      }
    }),
    [handleDataChange, manualValidate, value]
  );

  const dailyLimitText = isNotUndefinedOrNull(dailyRemainLimit) ? (
    `Daily limit remaining ${formatMoneyDisplay(convertCentToDollar(dailyRemainLimit))}`
  ) : (
    <Skeleton
      aria-label="Loading daily limit"
      width={300}
    />
  );

  const handleChange = (newValue) => {
    dispatch({ type: ACTION_TYPES.SET_VALUE, payload: newValue });
    const isError = !autoValidate(newValue);
    handleDataChange({ value: newValue, isError });
  };

  return (
    <Stack gap="16px">
      <Stack gap="4px">
        <Text
          color="primary"
          size="md"
          weight="medium"
        >
          {title}
        </Text>
        <Text
          color="primary"
          size="sm"
        >
          {dailyLimitText}
        </Text>
      </Stack>
      <MoneyInputBase
        defaultValue={defaultValue}
        error={!!errorMessage}
        onChangeValue={handleChange}
      />
      {errorMessage}
    </Stack>
  );
});

SendMoneyInput.propTypes = {
  title: PropTypes.string.isRequired,
  inputRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.node, PropTypes.any])
  }),
  defaultValue: PropTypes.number.isRequired,
  balance: PropTypes.number,
  dailyRemainLimit: PropTypes.number,
  dailyLimit: PropTypes.number,
  monthlyRemainLimit: PropTypes.number,
  monthlyLimit: PropTypes.number,
  inputType: PropTypes.oneOf([INPUT_TYPES.SEND_MONEY, INPUT_TYPES.WIRE_MONEY]),
  handleDataChange: PropTypes.func,
  addFunds: PropTypes.func,
  fee: PropTypes.number
};

export default SendMoneyInput;
